import {
  addUserToCollection,
  cancelInfluencerManualMassDm,
  createCollection,
  editDisplayName,
  fetchCollections,
  fetchCollectionUsers,
  fetchInfluencerChat,
  fetchInfluencerChats,
  fetchInfluencerMassDmHistory,
  fetchInfluencerNotificationStatus,
  fetchNotifications,
  getAiChatSuggestions,
  markChatAsRead,
  pinUser,
  sendMassDMMessage,
  sendMessage,
  toggleUserInfluencerManualChat,
  unblockUserInfluencerChat,
} from './asyncThunks';

export const fetchCollectionsBuilder = builder => {
  builder.addCase(fetchCollections.pending, state => {
    state.fetchCollectionsInfo.pending = true;
  });
  builder.addCase(fetchCollections.fulfilled, (state, { payload }) => {
    const { data } = payload;
    state.fetchCollectionsInfo.data = data;
    state.fetchCollectionsInfo.pending = false;
  });
  builder.addCase(fetchCollections.rejected, state => {
    state.fetchCollectionsInfo.pending = false;
  });
};

export const createCollectionBuilder = builder => {
  builder.addCase(createCollection.pending, state => {
    state.pending.createCollection = true;
  });
  builder.addCase(createCollection.fulfilled, state => {
    state.pending.createCollection = false;
  });
  builder.addCase(createCollection.rejected, state => {
    state.pending.createCollection = false;
  });
};

export const fetchCollectionUsersBuilder = builder => {
  builder.addCase(fetchCollectionUsers.pending, (state, { meta }) => {
    const page = meta.arg.page;
    if (page === 1) {
      state.fetchCollectionUsersInfo.pending = true;
    } else {
      state.fetchCollectionUsersInfo.fetchMorePending = true;
    }
  });
  builder.addCase(fetchCollectionUsers.fulfilled, (state, { payload }) => {
    const { data, maxReached, page } = payload;
    if (page === 1) {
      state.fetchCollectionUsersInfo.data = data;
      state.fetchCollectionUsersInfo.pending = false;
    } else {
      state.fetchCollectionUsersInfo.fetchMorePending = false;
      state.fetchCollectionUsersInfo.data = [...state.fetchCollectionUsersInfo.data, ...data];
    }
    state.fetchCollectionUsersInfo.maxReached = maxReached || !data.length;
  });
  builder.addCase(fetchCollectionUsers.rejected, state => {
    state.fetchCollectionUsersInfo.pending = false;
    state.fetchCollectionUsersInfo.fetchMorePending = false;
  });
};

export const fetchInfluencerChatsBuilder = builder => {
  builder.addCase(fetchInfluencerChats.pending, (state, { meta }) => {
    const page = meta.arg.page;
    const refresh = meta.arg.refresh;
    if (!refresh) {
      if (page === 1) {
        state.fetchInfluencerChatsInfo.pending = true;
      } else {
        state.fetchInfluencerChatsInfo.fetchMorePending = true;
      }
    } else {
      state.fetchInfluencerChatsInfo.refreshing = true;
    }
  });
  builder.addCase(fetchInfluencerChats.fulfilled, (state, { payload }) => {
    const { data, maxReached, page, collectionId } = payload;
    if (state.selectedCollectionId !== collectionId) {
      return;
    }
    if (page === 1) {
      state.fetchInfluencerChatsInfo.data = data;
      state.fetchInfluencerChatsInfo.pending = false;
    } else {
      state.fetchInfluencerChatsInfo.fetchMorePending = false;
      state.fetchInfluencerChatsInfo.data = [...state.fetchInfluencerChatsInfo.data, ...data];
    }
    state.fetchInfluencerChatsInfo.maxReached = maxReached || !data.length;
    state.fetchInfluencerChatsInfo.refreshing = false;
  });
  builder.addCase(fetchInfluencerChats.rejected, state => {
    state.fetchInfluencerChatsInfo.pending = false;
    state.fetchInfluencerChatsInfo.fetchMorePending = false;
    state.fetchInfluencerChatsInfo.refreshing = false;
  });
};

export const fetchInfluencerChatBuilder = builder => {
  builder.addCase(fetchInfluencerChat.pending, (state, { meta }) => {
    const { cursor, bidirectional, refresh } = meta.arg;
    const isInitialLoad = !cursor || bidirectional;
    if (!refresh) {
      if (isInitialLoad) {
        state.fetchInfluencerChatInfo.pending = true;
      } else {
        state.fetchInfluencerChatInfo.fetchMorePending = true;
      }
    }
  });
  builder.addCase(fetchInfluencerChat.fulfilled, (state, { payload, meta }) => {
    const userInfluencerId = meta.arg.userInfluencerId;
    if (userInfluencerId !== state.activeUserInfluencerId) {
      return;
    }

    const { data, hasMore, nextCursor, userInfluencerData } = payload;
    const { cursor, bidirectional } = meta.arg;
    const isInitialLoad = !cursor || bidirectional;
    if (isInitialLoad) {
      state.userInfluencer = userInfluencerData;
      state.fetchInfluencerChatInfo.data = data;
      state.fetchInfluencerChatInfo.pending = false;
    } else {
      state.fetchInfluencerChatInfo.fetchMorePending = false;
      state.fetchInfluencerChatInfo.data = [...data, ...state.fetchInfluencerChatInfo.data];
    }
    state.fetchInfluencerChatInfo.hasMore = hasMore;
    state.fetchInfluencerChatInfo.nextCursor = nextCursor;
  });
  builder.addCase(fetchInfluencerChat.rejected, state => {
    state.fetchInfluencerChatInfo.pending = false;
    state.fetchInfluencerChatInfo.fetchMorePending = false;
  });
};

export const sendMessageBuilder = builder => {
  builder.addCase(sendMessage.pending, state => {
    state.pending.sendMessage = true;
  });
  builder.addCase(sendMessage.fulfilled, state => {
    state.pending.sendMessage = false;
  });
  builder.addCase(sendMessage.rejected, state => {
    state.pending.sendMessage = false;
  });
};

export const sendMassDMMessageBuilder = builder => {
  builder.addCase(sendMassDMMessage.pending, state => {
    state.pending.sendMassDMMessage = true;
  });
  builder.addCase(sendMassDMMessage.fulfilled, state => {
    state.pending.sendMassDMMessage = false;
  });
  builder.addCase(sendMassDMMessage.rejected, state => {
    state.pending.sendMassDMMessage = false;
  });
};

export const pinUserBuilder = builder => {
  builder.addCase(pinUser.pending, state => {
    state.userInfluencer.isPinned = !state.userInfluencer.isPinned;
    state.pending.pinUser = true;
  });
  builder.addCase(pinUser.fulfilled, state => {
    state.pending.pinUser = false;
  });
  builder.addCase(pinUser.rejected, state => {
    state.userInfluencer.isPinned = !state.userInfluencer.isPinned;
    state.pending.pinUser = false;
  });
};

export const editDisplayNameBuilder = builder => {
  builder.addCase(editDisplayName.pending, state => {
    state.pending.editDisplayName = true;
  });
  builder.addCase(editDisplayName.fulfilled, (state, { payload }) => {
    const { name } = payload;
    state.userInfluencer.displayName = name;
    state.pending.editDisplayName = false;
  });
  builder.addCase(editDisplayName.rejected, state => {
    state.pending.editDisplayName = false;
  });
};

export const addUserToCollectionBuilder = builder => {
  builder.addCase(addUserToCollection.pending, state => {
    state.pending.addUserToCollection = true;
  });
  builder.addCase(addUserToCollection.fulfilled, state => {
    state.pending.addUserToCollection = false;
  });
  builder.addCase(addUserToCollection.rejected, state => {
    state.pending.addUserToCollection = false;
  });
};

export const fetchNotificationsBuilder = builder => {
  builder.addCase(fetchNotifications.pending, state => {
    state.fetchNotificationsInfo.pending = true;
  });
  builder.addCase(fetchNotifications.fulfilled, (state, { payload }) => {
    const { data, total } = payload;
    state.fetchNotificationsInfo.pending = false;
    state.fetchNotificationsInfo.data = data;
    state.fetchNotificationsInfo.total = total;
  });
  builder.addCase(fetchNotifications.rejected, state => {
    state.fetchNotificationsInfo.pending = false;
  });
};

export const fetchInfluencerNotificationStatusBuilder = builder => {
  builder.addCase(fetchInfluencerNotificationStatus.fulfilled, (state, { payload }) => {
    const { newNotifications } = payload;
    state.hasNewNotifications = newNotifications;
  });
};

export const toggleUserInfluencerManualChatBuilder = builder => {
  builder.addCase(toggleUserInfluencerManualChat.pending, state => {
    state.pending.toggleUserInfluencerManualChat = true;
  });
  builder.addCase(toggleUserInfluencerManualChat.fulfilled, state => {
    state.pending.toggleUserInfluencerManualChat = false;
  });
  builder.addCase(toggleUserInfluencerManualChat.rejected, state => {
    state.pending.toggleUserInfluencerManualChat = false;
  });
};

export const getAiChatSuggestionsBuilder = builder => {
  builder.addCase(getAiChatSuggestions.pending, state => {
    state.pending.getAiChatSuggestions = true;
  });
  builder.addCase(getAiChatSuggestions.fulfilled, state => {
    state.pending.getAiChatSuggestions = false;
  });
  builder.addCase(getAiChatSuggestions.rejected, state => {
    state.pending.getAiChatSuggestions = false;
  });
};

export const markChatAsReadBuilder = builder => {
  builder.addCase(markChatAsRead.pending, state => {
    state.pending.markChatAsRead = true;
  });
  builder.addCase(markChatAsRead.fulfilled, (state, { payload }) => {
    const { userInfluencerId } = payload;
    state.fetchInfluencerChatsInfo.data = state.fetchInfluencerChatsInfo.data.map(chat => {
      if (chat.id === userInfluencerId) {
        return { ...chat, numOfUnseenMessages: 0 };
      } else {
        return chat;
      }
    });
    state.pending.markChatAsRead = false;
  });
  builder.addCase(markChatAsRead.rejected, state => {
    state.pending.markChatAsRead = false;
  });
};

export const unblockUserInfluencerChatBuilder = builder => {
  builder.addCase(unblockUserInfluencerChat.pending, state => {
    state.pending.unblockUserInfluencerChat = true;
  });
  builder.addCase(unblockUserInfluencerChat.fulfilled, state => {
    state.pending.unblockUserInfluencerChat = false;
  });
  builder.addCase(unblockUserInfluencerChat.rejected, state => {
    state.pending.unblockUserInfluencerChat = false;
  });
};

export const fetchInfluencerMassDmHistoryBuilder = builder => {
  builder.addCase(fetchInfluencerMassDmHistory.pending, (state, { meta }) => {
    const page = meta.arg.page;
    if (page === 1) {
      state.fetchInfluencerMassDmHistoryInfo.pending = true;
    } else {
      state.fetchInfluencerMassDmHistoryInfo.fetchMorePending = true;
    }
  });
  builder.addCase(fetchInfluencerMassDmHistory.fulfilled, (state, { payload }) => {
    const { data, maxReached, page } = payload;
    if (page === 1) {
      state.fetchInfluencerMassDmHistoryInfo.data = data;
    } else {
      state.fetchInfluencerMassDmHistoryInfo.data = [...data, ...state.fetchInfluencerMassDmHistoryInfo.data];
    }
    state.fetchInfluencerMassDmHistoryInfo.maxReached = maxReached;
    state.fetchInfluencerMassDmHistoryInfo.pending = false;
    state.fetchInfluencerMassDmHistoryInfo.fetchMorePending = false;
  });
  builder.addCase(fetchInfluencerMassDmHistory.rejected, state => {
    state.fetchInfluencerMassDmHistoryInfo.pending = false;
    state.fetchInfluencerMassDmHistoryInfo.fetchMorePending = false;
  });
};

export const cancelInfluencerManualMassDmBuilder = builder => {
  builder.addCase(cancelInfluencerManualMassDm.pending, state => {
    state.pending.cancelInfluencerManualMassDm = true;
  });
  builder.addCase(cancelInfluencerManualMassDm.fulfilled, (state, { payload }) => {
    const { massDmId } = payload;
    state.fetchInfluencerMassDmHistoryInfo.data = state.fetchInfluencerMassDmHistoryInfo.data.map(m => {
      if (m._id === massDmId) {
        return { ...m, cancelledAt: new Date().toISOString() };
      } else {
        return m;
      }
    });
    state.pending.cancelInfluencerManualMassDm = false;
  });
  builder.addCase(cancelInfluencerManualMassDm.rejected, state => {
    state.pending.cancelInfluencerManualMassDm = false;
  });
};
